import gsap from "gsap";

const SCROLL_SURFACE_SELECTOR = ".inora-scroll-surface";

export type LegalScrollRoot =
  | { kind: "window" }
  | { kind: "element"; element: HTMLElement };

function getMarketingHeaderScrollOffset(): number {
  if (typeof window === "undefined") return 120;
  const root = document.documentElement;
  const styles = getComputedStyle(root);
  const headerTop =
    Number.parseFloat(styles.getPropertyValue("--header-top")) || 16;
  const headerHeight =
    Number.parseFloat(styles.getPropertyValue("--header-height")) || 64;
  return headerTop + headerHeight + 32;
}

export function resolveLegalScrollRoot(from: HTMLElement): LegalScrollRoot {
  const surface = from.closest(SCROLL_SURFACE_SELECTOR) as HTMLElement | null;
  if (surface && surface.scrollHeight > surface.clientHeight + 1) {
    return { kind: "element", element: surface };
  }
  return { kind: "window" };
}

function getScrollOffset(root: LegalScrollRoot): number {
  if (root.kind === "element") {
    return 24;
  }
  return getMarketingHeaderScrollOffset();
}

/** Smooth scroll with section heading below chrome (window or app scroll surface). */
export function scrollToLegalSection(id: string) {
  const el =
    document.getElementById(id) ??
    document.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`);
  if (!el) return;

  const root = resolveLegalScrollRoot(el);
  const offset = getScrollOffset(root);

  if (root.kind === "window") {
    const targetY =
      el.getBoundingClientRect().top + window.scrollY - offset;
    const state = { y: window.scrollY };
    gsap.to(state, {
      y: Math.max(0, targetY),
      duration: 0.9,
      ease: "power3.out",
      onUpdate: () => {
        window.scrollTo(0, state.y);
      },
    });
    return;
  }

  const container = root.element;
  const targetTop =
    container.scrollTop +
    el.getBoundingClientRect().top -
    container.getBoundingClientRect().top -
    offset;

  const state = { y: container.scrollTop };
  gsap.to(state, {
    y: Math.max(0, targetTop),
    duration: 0.9,
    ease: "power3.out",
    onUpdate: () => {
      container.scrollTop = state.y;
    },
  });
}

/** IntersectionObserver root when legal content lives in the app scroll shell. */
export function getLegalScrollObserverRoot(): Element | null {
  if (typeof document === "undefined") return null;
  return document.querySelector(SCROLL_SURFACE_SELECTOR);
}
